<template>
  <li
    class="present-item"
    :class="{ '--is-static': !id, '--is-unavailable': productIsUnavailable(product) }"
  >
    <div class="present-item__wrapper">
      <div
        @click="goTo()"
        class="present-item__photo"
      >
        <img
          :srcset="`
            ${photo.src}?format=${webPFormat('jpeg')}&width=96&nearlossless=1 2x,
            ${photo.src}?format=${webPFormat('jpeg')}&width=48&nearlossless=1 1x
          `"
          :src="`${photo.src}?format=jpeg&width=48`"
          :alt="photo.alt"
        >

        <img
          v-if="product && getFlagUrl(product.origin_iso.toLowerCase()) !== ''"
          :src="getFlagUrl(product.origin_iso.toLowerCase())"
          :title="`Produkt aus ${product.origin_country}`"
          class="present-item__flag"
        >
      </div>

      <p
        @click="goTo()"
        class="present-item__info"
      >
        <span class="present-item__name notranslate">{{ name }}</span>
        <br class="u-dp-none">
        <small class="present-item__description" v-html="description"></small>
      </p>

      <div
        v-if="price && $store.getters.getCurrentLevel === 3"
        class="present-item__price"
      >
        <Price>
          <template v-slot:netto>{{ price.netto }}</template>
          <template v-slot:brutto>{{ price.brutto }}</template>
        </Price>
      </div>

      <div
        v-if="id && $store.getters.getCurrentLevel === 3"
        class="present-item__actions"
      >
        <button @click="removeProduct(index)" class="present-item__btn-remove" title="Produkt entfernen"><i class="icon icon-times"></i></button>
      </div>
    </div>

    <div
      v-if="showReplaceNotice"
      class="present-item__notice"
    >
      <small><i class="icon icon-warning"></i> Sortenwechsel</small>
    </div>
    <div
      v-else-if="productIsUnavailable(product)"
      class="present-item__notice"
    >
      <small><i class="icon icon-warning"></i> Momentan nicht in der gewünschten Menge vorrätig.</small>
    </div>

    <div
      v-if="$store.getters.isAdminMode && $store.getters.getCurrentLevel === 1 && sku !== '' && $store.getters.getAdminData.Gerichshain[sku]"
      class="present-item__admin-tools"
    >
      <small>{{ JSON.stringify($store.getters.getAdminData.Gerichshain[sku], undefined, 1).replace('{', 'Gerichshain:').replace('}', '').replaceAll('"', '') }}</small>
    </div>
  </li>
</template>

<script>
import { logicMixin } from '@/mixins/LogicMixin';
import { helperMixin } from '@/mixins/HelperMixin';

import Price from '@/components/Common/Price';

export default {
  name: 'ModulePresentItem',
  mixins: [logicMixin, helperMixin],
  props: {
    product: {
    },
    index: {
      type: Number
    },
    id: {
      type: Number,
      default: 0
    },
    photo: {
      type: Object
    },
    name: {
      type: String
    },
    description: {
      type: String
    },
    price: {
      type: Object
    },
    linkTo: {
      type: String,
      default: ''
    },
    sku: {
      type: String,
      default: ''
    },
    showReplaceNotice: {
      type: Boolean,
      default: false,
    }
  },
  components: {
    Price
  },
  data () {
    return {
    };
  },
  methods: {
    goTo () {
      if (this.linkTo) {
        this.$router.push(this.cLink(this.linkTo));
      } else if (!this.linkTo && this.id) {
        if (this.$store.getters.getComposerEnvironment === 'configurator') {
          if (this.$store.getters.getSelectedItem.data !== null && this.$store.getters.getSelectedItem.data.type === 'level_1') {
            this.$router.push(this.cLink(`/fertige/${this.$store.getters.getSelectedItem.data.id}/details/${this.id}`));
          } else if (this.$store.getters.getSelectedItem.data !== null && this.$store.getters.getSelectedItem.data.type === 'level_3') {
            this.$router.push(this.cLink(`/zusammenstellen/${this.id}/details`));
          }
        } else if (this.$store.getters.getComposerEnvironment === 'present') {
          this.$router.push(this.cLink('/fertige/' + this.$store.getters.getSelectedItem.data.id + '/' + this.id));
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
/**
 * Present Item
 *
 * @author juanpablob <m.juanpablob@gmail.com>
 * @desc Present's item (used for listing products)
 * @since 2021-09-15
**/

$component: 'present-item';
$listVerticalSpace: map-get($spacers, 1) * 2.8;

.#{$component} {
  width: 100%;

  &:not(:last-child) {
    @include margin-bottom($listVerticalSpace);
  }

  &__wrapper {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    width: 100%;
  }

  &__wrapper > a {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    width: 100%;
    color: $ink;
  }

  $photo-width: 46px;
  $photo-margin: 10px;

  &__photo {
    position: relative;
    flex-grow: 0;
    flex-shrink: 0;
    flex-basis: 2.8rem;
    aspect-ratio: 1 / 1;
    @include margin-right(map-get($spacers, 2));
    @include padding(0.1rem);
    border: 1px solid $gray-ghost;
    border-radius: $border-radius-sm;
    background-color: $white;
    cursor: pointer;
    // @include imageRendering;

    img:first-child {
      display: block;
      width: 100%;
      aspect-ratio: 1 / 1;
      object-fit: cover;
    }
  }

  &__flag {
    position: absolute;
    top: -6px;
    left: -6px;
    width: 18px;
    height: 18px;
    border: 2px solid white;
    border-radius: 50%;
  }

  &__info {
    display: flex;
    min-width: 0;
    flex-direction: column;
    flex-grow: 1;
    flex-shrink: 1;
    margin: 0;
    cursor: pointer;
  }

  &__name {
    width: 100%;
    overflow: hidden;
    @include margin-bottom(map-get($spacers, 1) / 2);
    white-space: nowrap;
    text-overflow: ellipsis;
    line-height: $line-height-base-sm;
  }

  &__description {
    width: 100%;
    overflow: hidden;
    margin-top: -2px;
    white-space: nowrap;
    text-overflow: ellipsis;
    color: $ink-lighter;
  }

  &__price {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    flex-shrink: 0;
    @include font-size($font-size-sm);
    text-align: right;

    small {
      font-weight: $font-weight-light;
    }
  }

  &__price-line1 {
    font-weight: $font-weight-bold;
  }

  &__price-line2 {
    color: $ink-lightest;
  }

  &__actions {
    align-self: center;
    @include margin-left(map-get($spacers, 1));
  }

  &__btn-remove {
    display: flex;
    align-items: center;
    justify-content: center;
    aspect-ratio: 1 / 1;
    margin: 0;
    padding: 0;
    border: 0;
    border-radius: 50%;
    outline: 4px solid transparent;
    background: $red;
    color: $white;
    @include font-size($font-size-lg);

    .icon {
      line-height: 1;
      margin-left: -1px;
    }
  }

  &__admin-tools {
    margin-left: 2.5rem;
    @include padding-left(map-get($spacers, 2));
    color: $ink-lighter;
    line-height: $line-height-base-sm;

    > small {
      display: block;

      &:before {
        content: '';
        display: inline-block;
        width: 5px;
        height: 5px;
        vertical-align: middle;
        @include margin-right(map-get($spacers, 1));
        border-radius: 50%;
        background-color: $blue;
      }
    }

    > br {
      display: none;
    }
  }

  &.--is-static {
    align-items: flex-start;

    &:not(:first-child) {
      @include padding-top($listVerticalSpace);
      border-top: 1px solid $gray-ghost;
    }

    .#{$component} {
      &__photo {
        border-color: transparent;
        @include margin-top(- 0.1rem);
        @include margin-bottom(- 0.1rem);
      }

      &__name {
        font-weight: $font-weight-bold;
      }

      &__name,
      &__description {
        overflow: visible;
        white-space: unset;
        text-overflow: unset;
      }
    }
  }

    &__notice {
      margin-left: 2.5rem;
      @include padding-left(map-get($spacers, 2));
      color: darken($yellow, 50);

      .icon {
        @include padding(map-get($spacers, 1));
        border-radius: 50%;
        background-color: $yellow;
      }
    }

  &.--is-unavailable {
    .#{$component} {
      &__photo,
      &__info,
      &__price {
        opacity: 0.5;
      }
    }
  }

  /** RESPONSIVENESS **/

  // ——————————————————————————————
  // Max LG
  // ——————————————————————————————
  @media (max-width: map-get($landing-page, 'viewport-lg')) {
  }

  // ——————————————————————————————
  // Max MD
  // ——————————————————————————————
  @media (max-width: map-get($landing-page, 'viewport-md')) {
  }

  // ——————————————————————————————
  // Max SM
  // ——————————————————————————————
  @media (max-width: map-get($landing-page, 'viewport-sm')) {
  }

  // ——————————————————————————————
  // Max XS
  // ——————————————————————————————
  @media (max-width: map-get($landing-page, 'viewport-xs')) {
    &__name {
      @include margin-bottom(map-get($spacers, 1));
      line-height: $line-height-base-sm;
    }
  }
}
</style>
